import { useCallback, useEffect, useState } from "react";
import { Button, Drawer, drawerClasses, IconButton, Stack, styled, Typography } from "~/common/mui";
import { Close } from "@mui/icons-material";

import { Chip } from "~/common/mui/Chip/Chip";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { verticalStacker } from "~/styles/styledMixins";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ReduxSortingPicker from "~/features/search/components/common/ReduxSortingPicker";
import { ReduxFilterSection } from "~/features/search/components/Filters/ReduxFilterSection";
import { useSelectAppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { applyAllFiltersAction, clearAll } from "~/features/search/redux/actions";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { useForm } from "react-hook-form";
import {
  flattenTechFilterFormData,
  unflattenTechFilterFormData,
} from "~/features/search/utils/flattenTechFilterFormData";
import { DialogActions, DialogContent } from "~/common/mui/Dialog/Dialog";
import { selectAppliedTechFilterValues } from "~/features/search/redux/searchSelectors";

export const FilterSortChip = () => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const isMinTablet = useIsMinTabletSize();
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);

  const methods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  useEffect(() => {
    if (isDrawerOpen) {
      methods.reset();
    }
  }, [appliedTechFilterValues, methods, isDrawerOpen]);

  const pageType = useSearchPageType();

  const handleClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleClearAll = useCallback(() => {
    dispatch(clearAll(pageType));
  }, [dispatch, pageType]);

  const onSubmit = (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
    const flattenedData = flattenTechFilterFormData(data);
    dispatch(applyAllFiltersAction(flattenedData));
    handleClose();
  };

  const applyCb = methods.handleSubmit(onSubmit);

  const breadcrumbLength = useSelectAppliedFilters().length;
  let label;
  if (breadcrumbLength === 0) {
    label = t("page.search.chip.filter.empty");
  } else {
    label = t("page.search.chip.filter.selected", { count: breadcrumbLength });
  }
  return (
    <>
      <Chip
        variant={"filter"}
        onClick={() => setIsDrawerOpen(true)}
        icon={<FilterAltOutlinedIcon />}
        label={label}
        selected={breadcrumbLength > 0}
      />
      <StyledDrawer anchor={isMinTablet ? "left" : "bottom"} open={isDrawerOpen} onClose={handleClose}>
        <DrawerContent sx={{ ...verticalStacker("24px") }}>
          <DrawerHeader>
            <Typography
              variant="titleLarge"
              sx={{
                fontWeight: "bold",
              }}
            >
              Filter & sort
            </Typography>
            <IconButton onClick={handleClose} color={"secondary"} variant={"contained"}>
              <Close />
            </IconButton>
          </DrawerHeader>
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Typography
              variant={"titleMedium"}
              sx={{
                fontWeight: "bold",
              }}
            >
              {t("searchPage.sort.title")}
            </Typography>
            <ReduxSortingPicker />
          </Stack>

          <form onSubmit={applyCb}>
            <ReduxFilterSection
              titleFontVariant={"titleMedium"}
              hideTechFilterButtons
              methods={methods}
              onSubmit={onSubmit}
            />
          </form>
        </DrawerContent>

        <DrawerActions>
          <Button variant="text" fullWidth onClick={handleClearAll}>
            {t("searchPage.refinement.button.clearAll")}
          </Button>
          <Button fullWidth onClick={applyCb} disabled={!methods.formState.isDirty}>
            {t("searchPage.refinement.button.apply")}
          </Button>
        </DrawerActions>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "90vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.up("tablet")]: {
      width: "45vw",
      maxWidth: "550px",
    },
  },
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  position: "sticky",
  top: "0",
  paddingTop: "16px",
  zIndex: "5",
  background: theme.palette.surface.bright,
}));

const DrawerContent = styled(DialogContent)(({ theme }) => ({
  padding: "0 16px 16px 16px",
  backgroundColor: theme.palette.surface.bright,
}));

const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
  zIndex: 1,
});
